/**
 * ChatActionContext — Exposes sidebar chat actions to A2UI render components.
 *
 * CopilotKit `useCopilotAction` render components live in CopilotKit's
 * React tree but still inherit context from ancestors. This context
 * bridges `sendMessage` from the sidebar to any child component
 * (e.g., MutationResultCard next-action buttons).
 */
import React, { createContext, useContext } from 'react';

interface ChatActionContextValue {
  /** Send a user message to the chat (triggers MetaAgent processing). */
  sendChatMessage: (text: string) => void;
}

const ChatActionContext = createContext<ChatActionContextValue | undefined>(undefined);

export const ChatActionProvider = ChatActionContext.Provider;

/** Use inside A2UI render components to send messages to the chat. */
export const useChatAction = (): ChatActionContextValue => {
  const ctx = useContext(ChatActionContext);
  if (!ctx) {
    // Fallback: if rendered outside provider, no-op instead of crash
    // (can happen during CopilotKit message hydration before provider mounts)
    return { sendChatMessage: () => {} };
  }
  return ctx;
};
